import { FetchVendorsResponse, initialVendor, Vendor, VendorKey } from '@api/models'
import { toast, useModal } from '@components/common'
import {
  useCreateVendorMutation,
  useDeleteVendorMutation,
  useEditVendorMutation,
  useFetchVendorsQuery,
  useUploadAttachmentMutation,
  useDeleteAttachmentMutation,
} from '@api'
import { Dispatch, SetStateAction, useState } from 'react'
import { useTableParams } from '@hooks'
import { TableParamsHook } from 'hooks/useTableParams'
import {
  StagedAttachment,
  VendorFormSubmissionData,
} from '@api/attachments/models/Attachment.model'
import { useAppDispatch } from '../../index'
import { attachmentsEndpoints } from '@api/attachments/attachmentsEndpoints'

export interface VendorsHook extends TableParamsHook {
  filterState: Object
  setFilterState: Dispatch<SetStateAction<{}>>
  searchKey: string
  selectedVendor: Vendor

  vendors: FetchVendorsResponse
  isLoadingVendors: boolean
  isErrorFetchingVendors: boolean
  isRefetchingVendors: boolean

  handleEditVendor: (payload: VendorFormSubmissionData) => Promise<void>
  handleCreateVendor: (payload: VendorFormSubmissionData) => Promise<void>
  isLoadingEditVendor: boolean
  isLoadingCreateVendor: boolean

  handleDeleteVendor: (payload: Vendor) => Promise<void>
  isLoadingDeleteVendor: boolean

  deleteVendorModalIsOpen: boolean
  triggerDeleteVendorModal
  closeDeleteVendorModal
  openDeleteVendorModal: (vendor: Vendor) => void
  openEditVendorModal: (vendor: Vendor) => void
  triggerEditVendorModal
  closeEditVendorModal
  editVendorModalIsOpen: boolean

  openCreateVendorModal: () => void
  createVendorModalIsOpen: boolean
  closeCreateVendorModal
}

export default function useVendors(): VendorsHook {
  const [filterState, setFilterState] = useState({})
  const [selectedVendor, setSelectedVendor] = useState<Vendor>(undefined)
  const dispatch = useAppDispatch()

  const searchKey: VendorKey = 'VN_NAME'
  const initialItemsPerPage = 20

  // Use useTableParams hook for table filtering, pagination, etc.
  const {
    pageNumber,
    itemsPerPage,
    fetchRequestPayload,
    handleSearch,
    searchValue,
    changePage,
    sortByInitialState,
    updateSortSearchParam,
  } = useTableParams({
    searchKey,
    filterState,
    setFilterState,
    initialItemsPerPage,
  })

  // Fetch Vendors
  const {
    data: vendors,
    isLoading: isLoadingVendors,
    isError: isErrorFetchingVendors,
    isFetching: isRefetchingVendors,
  } = useFetchVendorsQuery(fetchRequestPayload)

  // MODAL STATES
  // Delete modal
  const {
    modalIsOpen: deleteVendorModalIsOpen,
    triggerModal: triggerDeleteVendorModal,
    closeModal: closeDeleteVendorModal,
  } = useModal()
  function openDeleteVendorModal(vendor: Vendor): void {
    setSelectedVendor(vendor)
    triggerDeleteVendorModal()
  }

  // Edit modal
  const {
    modalIsOpen: editVendorModalIsOpen,
    triggerModal: triggerEditVendorModal,
    closeModal: closeEditVendorModal,
  } = useModal()

  function openEditVendorModal(vendor: Vendor): void {
    setSelectedVendor(vendor)
    triggerEditVendorModal()
  }

  // Create modal
  const {
    modalIsOpen: createVendorModalIsOpen,
    triggerModal: triggerCreateVendorModal,
    closeModal: closeCreateVendorModal,
  } = useModal()
  function openCreateVendorModal(): void {
    setSelectedVendor(initialVendor)
    triggerCreateVendorModal()
  }

  // MUTATIONS
  // Delete
  const [deleteVendor, { isLoading: isLoadingDeleteVendor }] = useDeleteVendorMutation()
  const [deleteAttachment] = useDeleteAttachmentMutation()

  async function handleDeleteVendor(payload: Vendor): Promise<void> {
    try {
      const getVendorAttachmentsResult = await dispatch(
        attachmentsEndpoints.endpoints.getVendorAttachments.initiate({
          vendorID: payload.VN_VEN_ID,
        })
      )

      const attachments = getVendorAttachmentsResult.data || []

      await deleteVendor(payload).unwrap()
      closeDeleteVendorModal()

      if (attachments.length > 0) {
        const attachmentDeletionPromises = attachments.map(attachment =>
          deleteAttachment({
            id: attachment.ID,
            type: 'Vendor',
          }).unwrap()
        )

        try {
          await Promise.all(attachmentDeletionPromises)
          toast.success(
            `Successfully deleted "${payload.VN_NAME}" and ${attachments.length} associated attachment(s) from your vendors.`
          )
        } catch (attachmentError) {
          console.warn('Some attachments failed to delete:', attachmentError)
          toast.success(
            `Successfully deleted "${payload.VN_NAME}" from your vendors, but ${attachments.length} attachment(s) could not be removed.`
          )
        }
      } else {
        toast.success(`Successfully deleted "${payload.VN_NAME}" from your vendors.`)
      }
    } catch (error: any) {
      toast.error(error.data || 'There was an unexpected error deleting this vendor.')
    }
  }

  // Edit
  const [editVendor, { isLoading: isLoadingEditVendor }] = useEditVendorMutation()
  async function handleEditVendor(data: VendorFormSubmissionData): Promise<void> {
    try {
      await editVendor(data.vendorData).unwrap()

      // Upload staged attachments if any
      if (data.stagedAttachments.length > 0) {
        const uploadResults = await uploadStagedAttachments(
          data.vendorData.VN_VEN_ID,
          data.stagedAttachments
        )

        if (uploadResults.failed.length > 0) {
          toast.error(
            `Vendor "${data.vendorData.VN_NAME}" updated successfully, but ${uploadResults.failed.length} attachment(s) failed to upload.`
          )
        } else {
          toast.success(
            `Vendor "${data.vendorData.VN_NAME}" successfully updated with ${uploadResults.successful} attachment(s).`
          )
        }
      } else {
        toast.success(`Vendor "${data.vendorData.VN_NAME}" successfully updated.`)
      }

      closeEditVendorModal()
    } catch (error: any) {
      toast.error(error.data || 'There was an unexpected error updating this vendor.')
    }
  }

  // Create
  const [createVendor, { isLoading: isLoadingCreateVendor }] = useCreateVendorMutation()
  const [uploadAttachment] = useUploadAttachmentMutation()

  async function uploadStagedAttachments(
    vendorID: string,
    stagedAttachments: StagedAttachment[]
  ): Promise<{ successful: number; failed: StagedAttachment[] }> {
    const results = { successful: 0, failed: [] as StagedAttachment[] }

    const attachmentsToUpload = stagedAttachments.slice(0, 1)

    if (stagedAttachments.length > 1) {
      console.warn(
        'Only one attachment per vendor is allowed. Only the first attachment will be uploaded.'
      )
    }

    for (const staged of attachmentsToUpload) {
      try {
        const formData = new FormData()
        formData.append('file', staged.file)
        formData.append('assetCompanyID', '0') // Not needed for vendor
        formData.append('objectIDs', JSON.stringify([vendorID]))
        formData.append('type', 'Vendor')
        formData.append('comment', staged.comment)

        await uploadAttachment(formData).unwrap()
        results.successful++
      } catch (error) {
        results.failed.push(staged)
      }
    }

    if (stagedAttachments.length > 1) {
      results.failed.push(...stagedAttachments.slice(1))
    }

    return results
  }

  async function handleCreateVendor(data: VendorFormSubmissionData): Promise<void> {
    try {
      // Step 1: Create the vendor
      await createVendor(data.vendorData).unwrap()

      // Step 2: Upload staged attachments if any
      if (data.stagedAttachments.length > 0) {
        const uploadResults = await uploadStagedAttachments(
          data.vendorData.VN_VEN_ID,
          data.stagedAttachments
        )

        if (uploadResults.failed.length > 0) {
          toast.error(
            `Vendor created successfully, but ${uploadResults.failed.length} attachment(s) failed to upload.`
          )
        } else if (uploadResults.successful > 0) {
          toast.success(
            `Successfully added ${data.vendorData.VN_NAME} to vendors with ${uploadResults.successful} attachment(s).`
          )
        } else {
          toast.success(`Successfully added ${data.vendorData.VN_NAME} to vendors.`)
        }
      } else {
        toast.success(`Successfully added ${data.vendorData.VN_NAME} to vendors.`)
      }

      closeCreateVendorModal()
    } catch (error: any) {
      toast.error(error.data || 'There was an unexpected error creating this vendor.')
    }
  }

  return {
    // tableParams return
    pageNumber,
    itemsPerPage,
    fetchRequestPayload,
    handleSearch,
    searchValue,
    changePage,
    sortByInitialState,
    updateSortSearchParam,
    //
    filterState,
    setFilterState,
    searchKey,
    selectedVendor,

    vendors,
    isLoadingVendors,
    isErrorFetchingVendors,
    isRefetchingVendors,

    handleDeleteVendor,
    isLoadingDeleteVendor,

    handleEditVendor,
    handleCreateVendor,
    isLoadingCreateVendor,
    isLoadingEditVendor,

    deleteVendorModalIsOpen,
    triggerDeleteVendorModal,
    closeDeleteVendorModal,
    openDeleteVendorModal,
    openEditVendorModal,
    triggerEditVendorModal,
    closeEditVendorModal,
    editVendorModalIsOpen,

    createVendorModalIsOpen,
    openCreateVendorModal,
    closeCreateVendorModal,
  }
}
